import api from './api';

class OrganizationService {
  constructor() {
    // Cache for recent requests to avoid duplicate calls
    this.requestCache = new Map();
    this.cacheTimeout = 5000; // 5 seconds cache
  }

  // Helper method for cached requests
  async cachedRequest(key, requestFn) {
    const cached = this.requestCache.get(key);
    const now = Date.now();
    
    // Return cached result if still valid
    if (cached && (now - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }
    
    // Make new request and cache result
    try {
      const result = await requestFn();
      this.requestCache.set(key, {
        data: result,
        timestamp: now
      });
      return result;
    } catch (error) {
      // Don't cache errors
      this.requestCache.delete(key);
      throw error;
    }
  }
  // Organization CRUD
  async createOrganization(organizationData) {
    try {
      const response = await api.post('/orgs', organizationData);
      return response.data;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw this.handleError(error);
    }
  }

  async getOrganizations() {
    try {
      const response = await api.get('/orgs');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw this.handleError(error);
    }
  }

  // User Profile
  async getUserWithOrganizations() {
    return this.cachedRequest('userWithOrganizations', async () => {
      try {
        const response = await api.get('/users/me/with-organizations');
        return response.data;
      } catch (error) {
        console.error('Error fetching user with organizations:', error);
        throw this.handleError(error);
      }
    });
  }

  async getOrganization(orgId) {
    try {
      const response = await api.get(`/orgs/${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw this.handleError(error);
    }
  }

  async updateOrganization(orgId, organizationData) {
    try {
      const response = await api.put(`/orgs/${orgId}`, organizationData);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw this.handleError(error);
    }
  }

  async deleteOrganization(orgId) {
    try {
      await api.delete(`/orgs/${orgId}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw this.handleError(error);
    }
  }

  // Organization Members
  async getOrganizationMembers(orgId) {
    try {
      const response = await api.get(`/orgs/${orgId}/members`);
      return response.data;
    } catch (error) {
      // Don't log cancelled/aborted requests
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED') {
        console.error('Error fetching organization members:', error);
      }
      throw this.handleError(error);
    }
  }

  async addOrganizationMember(orgId, memberData) {
    try {
      const response = await api.post(`/orgs/${orgId}/members`, memberData);
      return response.data;
    } catch (error) {
      console.error('Error adding organization member:', error);
      throw this.handleError(error);
    }
  }

  async updateOrganizationMember(orgId, userId, memberData) {
    try {
      const response = await api.put(`/orgs/${orgId}/members/${userId}`, memberData);
      return response.data;
    } catch (error) {
      console.error('Error updating organization member:', error);
      throw this.handleError(error);
    }
  }

  async removeOrganizationMember(orgId, userId) {
    try {
      await api.delete(`/orgs/${orgId}/members/${userId}`);
      return { success: true };
    } catch (error) {
      console.error('Error removing organization member:', error);
      throw this.handleError(error);
    }
  }

  // Organization Invitations
  async getOrganizationInvitations(orgId) {
    try {
      const response = await api.get(`/orgs/${orgId}/invitations`);
      return response.data;
    } catch (error) {
      // Don't log cancelled/aborted requests
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED') {
        console.error('Error fetching organization invitations:', error);
      }
      throw this.handleError(error);
    }
  }

  async createOrganizationInvitation(orgId, invitationData) {
    try {
      const response = await api.post(`/orgs/${orgId}/invitations`, invitationData);
      return response.data;
    } catch (error) {
      console.error('Error creating organization invitation:', error);
      throw this.handleError(error);
    }
  }

  async createBulkOrganizationInvitations(orgId, bulkInvitationData) {
    try {
      const response = await api.post(`/orgs/${orgId}/invitations/bulk`, bulkInvitationData);
      return response.data;
    } catch (error) {
      console.error('Error creating bulk organization invitations:', error);
      throw this.handleError(error);
    }
  }

  async acceptOrganizationInvitation(token) {
    try {
      const response = await api.post('/orgs/invitations/accept', { token });
      return response.data;
    } catch (error) {
      console.error('Error accepting organization invitation:', error);
      throw this.handleError(error);
    }
  }

  async getInvitationDetails(token) {
    try {
      const response = await api.get(`/orgs/invitations/details?token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invitation details:', error);
      throw this.handleError(error);
    }
  }

  async cancelOrganizationInvitation(orgId, invitationId) {
    try {
      await api.delete(`/orgs/${orgId}/invitations/${invitationId}`);
      return { success: true };
    } catch (error) {
      console.error('Error canceling organization invitation:', error);
      throw this.handleError(error);
    }
  }

  // Alias for better API naming consistency
  async inviteOrganizationMember(orgId, invitationData) {
    return this.createOrganizationInvitation(orgId, invitationData);
  }

  
  async updateUserProfile(profileData) {
    try {
      const response = await api.put('/users/me/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw this.handleError(error);
    }
  }

  // Error handling
  handleError(error) {
    // Preserve cancelled/aborted errors as-is
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED') {
      return error;
    }
    
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.detail || 'Bad request');
        case 401:
          return new Error('Unauthorized - please log in again');
        case 403:
          return new Error(data.detail || 'Insufficient permissions');
        case 404:
          return new Error(data.detail || 'Resource not found');
        case 409:
          return new Error(data.detail || 'Conflict - resource already exists');
        case 422:
          // Validation errors
          if (data.detail && Array.isArray(data.detail)) {
            const messages = data.detail.map(err => err.msg || err.message).join(', ');
            return new Error(`Validation error: ${messages}`);
          }
          return new Error(data.detail || 'Validation error');
        case 500:
          return new Error('Server error - please try again later');
        default:
          return new Error(data.detail || `HTTP ${status} error`);
      }
    } else if (error.request) {
      // Network error
      return new Error('Network error - please check your connection');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred');
    }
  }
}

const organizationService = new OrganizationService();
export default organizationService;
